import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useDataFetch } from "../../../core/hooks/useDataFetch";
import { useResponsive } from "../../../core/hooks/useResponsive";
import { Config } from "../../../config/config";

const fmtPrice = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtInr = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
};

/** Zero prints as a plain dash-free 0 so a row reads as "neither", not "missing". */
const Amount = ({ value, tone }) => {
  const theme = useTheme();
  const v = Number(value);
  if (!Number.isFinite(v)) return <span style={{ color: theme.palette.text.disabled }}>—</span>;
  if (v === 0) return <span style={{ color: theme.palette.text.disabled }}>0</span>;
  return (
    <span style={{ color: theme.palette[tone].main, fontWeight: 700 }}>{fmtInr(v)}</span>
  );
};

function SummaryTile({ label, value, tone, icon: Icon, hint }) {
  const theme = useTheme();
  const colour = tone ? theme.palette[tone].main : theme.palette.text.primary;
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: { xs: "100%", sm: 160 },
        borderRadius: 2,
        p: { xs: 1.25, sm: 1.5 },
        border: `1px solid ${alpha(colour, 0.25)}`,
        bgcolor: alpha(colour, 0.06),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        {Icon && <Icon sx={{ fontSize: 18, color: colour }} />}
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography variant="h6" fontWeight={800} sx={{ color: colour, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Box>
  );
}

export default function ClosedTradeReviewDialog({ open, onClose }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { isPhone, isCompact } = useResponsive();
  const { callAPI } = useDataFetch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    callAPI(Config.ITradeAPI.closedTradePerformance)
      .then((res) => {
        if (cancelled) return;
        if (res?.status && res.data) setData(res.data);
        else setFailed(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [open]);

  const trades = data?.trades || [];
  const summary = data?.summary;
  const net = Number(summary?.netAfterExit);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isPhone}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: isPhone ? 0 : 3,
          maxHeight: isPhone ? "100%" : "92vh",
          border: `1px solid ${alpha(primary, 0.12)}`,
        },
      }}
    >
      <DialogTitle sx={{ pr: 6, py: { xs: 1.25, sm: 2 } }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <HistoryToggleOffIcon sx={{ color: primary }} />
          <Typography variant={isPhone ? "subtitle1" : "h6"} fontWeight={800}>
            After the exit
          </Typography>
          {summary?.totalTrades > 0 && (
            <Chip size="small" variant="outlined" label={`${summary.totalTrades} closed`} />
          )}
        </Stack>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1.25, sm: 2 } }}>
        {loading && (
          <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && failed && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            Could not load closed trades. Please close and try again.
          </Alert>
        )}

        {!loading && !failed && data?.message && trades.length === 0 && (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {data.message}
          </Alert>
        )}

        {!loading && !failed && trades.length > 0 && (
          <Stack spacing={{ xs: 1.25, sm: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <SummaryTile
                label="Loss escaped"
                value={fmtInr(summary.totalLossEscaped)}
                tone="success"
                icon={ShieldOutlinedIcon}
                hint="Selling avoided this"
              />
              <SummaryTile
                label="Profit missed"
                value={fmtInr(summary.totalProfitMissed)}
                tone="warning"
                icon={TrendingUpIcon}
                hint="Holding would have earned this"
              />
              <SummaryTile
                label="Net"
                value={fmtInr(summary.netAfterExit)}
                tone={net >= 0 ? "success" : "error"}
                hint={net >= 0 ? "Exits were worth it overall" : "Holding would have paid more"}
              />
            </Stack>

            {summary.priced < summary.totalTrades && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                No live price for {summary.totalTrades - summary.priced} of {summary.totalTrades} trades,
                so those rows are not counted in the totals.
              </Alert>
            )}

            <TableContainer
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(primary, 0.12)}`,
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                "& .MuiTableHead-root .MuiTableCell-root": {
                  bgcolor: alpha(primary, 0.07),
                  fontWeight: 800,
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  color: primary,
                  whiteSpace: "nowrap",
                },
                "& .MuiTableCell-root": {
                  px: { xs: 0.75, md: 1.5 },
                  fontSize: { xs: "0.72rem", md: "0.8125rem" },
                  whiteSpace: "nowrap",
                },
                // Symbol stays visible while the money columns scroll on a phone.
                "& .sticky-symbol": {
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  bgcolor: theme.palette.background.paper,
                },
                "& .MuiTableHead-root .sticky-symbol": { zIndex: 4 },
              }}
            >
              <Table size="small" stickyHeader sx={{ minWidth: { xs: 620, md: 760 } }}>
                <TableHead>
                  <TableRow>
                    {!isCompact && <TableCell>Token</TableCell>}
                    <TableCell className="sticky-symbol">Symbol</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Exit</TableCell>
                    <TableCell align="right">Current</TableCell>
                    <TableCell align="right">Loss escaped</TableCell>
                    <TableCell align="right">Profit missed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trades.map((row) => (
                    <TableRow key={row.tradeID} hover>
                      {!isCompact && <TableCell>{row.token}</TableCell>}
                      <TableCell className="sticky-symbol" sx={{ fontWeight: 700 }}>
                        {row.symbol}
                      </TableCell>
                      <TableCell align="right">{row.qty}</TableCell>
                      <TableCell align="right">{fmtPrice(row.exitPrice)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {row.priceAvailable ? fmtPrice(row.currentPrice) : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Amount value={row.priceAvailable ? row.lossEscaped : null} tone="success" />
                      </TableCell>
                      <TableCell align="right">
                        <Amount value={row.priceAvailable ? row.profitMissed : null} tone="warning" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary">
              Each closed trade compared with the token's current price. Loss escaped is what selling
              avoided; profit missed is what holding would have earned. Only one can apply to a row.
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 1.5, sm: 2 }, py: 1 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
